import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Paper,
  Chip,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const CommissionManagement = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  
  const [commissionRate, setCommissionRate] = useState('');
  const [minimumPay, setMinimumPay] = useState('');
  // Optional maximum pay for game cards (only for shops that activate it)
  const [maximumPayEnabled, setMaximumPayEnabled] = useState(false);
  const [maximumPay, setMaximumPay] = useState('');
  const [cashbackCount, setCashbackCount] = useState('');
  const [serviceAdvance, setServiceAdvance] = useState('');
  const [warningThreshold, setWarningThreshold] = useState('600');
  const [blockThreshold, setBlockThreshold] = useState('500');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [userId, setUserId] = useState(null);
  const { can } = usePermissions();
  const canSetCommission = can('merchants.commission.set');
  const canEditCommission = can('merchants.commission.edit');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending'); // 'pending' or 'set'

  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
      } catch (error) {
        setSnackbarMessage('Error decoding user token');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        navigate('/login');
      }
    } else {
      setSnackbarMessage('No user token found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (userId) {
      fetchShops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, pageSize, filterStatus]);

  const fetchShops = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: pageSize,
        externalSource: 'acemoney',
      };

      if (filterStatus === 'pending') {
        params.progress = '1';
      }

      const response = await axios.get(`${API_BASE_URL}/shops/${userId}`, { params });
      setShops(response.data.shops || []);
      setTotalRows(response.data.totalShops || 0);
    } catch (error) {
      const errMsg = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : 'Error fetching shops';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setShops([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClear = () => {
    setSearchText('');
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterStatus(newFilter);
      setPage(0);
    }
  };

  const filteredShops = shops.filter((shop) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const mId = (shop.acemoneyUsercode || shop.sellerIdentifier || shop.vpaCode || shop.merchantId || '').toLowerCase();
    const bName = (shop.businessName || '').toLowerCase();
    const sName = (shop.sellerName || '').toLowerCase();
    const pin = (shop.pincode || '').toLowerCase();
    const mob = (shop.mobileNumber || '').toLowerCase();

    return (
      mId.includes(searchLower) ||
      bName.includes(searchLower) ||
      sName.includes(searchLower) ||
      pin.includes(searchLower) ||
      mob.includes(searchLower)
    );
  });

  const handleSetCommissionOpen = (shop) => {
    if (!canSetCommission) return;
    setSelectedShop(shop);
    setIsEditMode(false);
    setCommissionRate(shop.commissionRate !== undefined && shop.commissionRate !== null ? shop.commissionRate.toString() : '');
    setMinimumPay(shop.minimumPay !== undefined && shop.minimumPay !== null ? shop.minimumPay.toString() : '');
    setCashbackCount(shop.cashbackCount !== undefined && shop.cashbackCount !== null ? shop.cashbackCount.toString() : '');
    setMaximumPayEnabled(Boolean(shop.maximumPayEnabled));
    setMaximumPay(shop.maximumPay ? shop.maximumPay.toString() : '');
    setServiceAdvance('');
    setWarningThreshold((shop.warningThreshold || 600).toString());
    setBlockThreshold((shop.blockThreshold || 500).toString());
    setDialogOpen(true);
  };

  const handleEditCommissionOpen = async (shop) => {
    if (!canEditCommission) return;
    setSelectedShop(shop);
    setIsEditMode(true);
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/shop-important-data/shop/${shop._id}/${userId}`);
      const commissionData = (response.data && response.data.shopImportantData) ? response.data.shopImportantData : (response.data || {});

      const cRate = commissionData.commissionRate !== undefined ? commissionData.commissionRate : (shop.commissionRate !== undefined ? shop.commissionRate : '');
      const minPay = commissionData.minimumPay !== undefined ? commissionData.minimumPay : (shop.minimumPay !== undefined ? shop.minimumPay : '');
      const cbCount = commissionData.cashbackCount !== undefined ? commissionData.cashbackCount : (shop.cashbackCount !== undefined ? shop.cashbackCount : '');
      const warnTh = commissionData.warningThreshold !== undefined ? commissionData.warningThreshold : (shop.warningThreshold || 600);
      const blkTh = commissionData.blockThreshold !== undefined ? commissionData.blockThreshold : (shop.blockThreshold || 500);

      setCommissionRate(cRate.toString());
      setMinimumPay(minPay.toString());
      setCashbackCount(cbCount.toString());
      setMaximumPayEnabled(Boolean(commissionData.maximumPayEnabled));
      setMaximumPay(commissionData.maximumPay ? commissionData.maximumPay.toString() : '');
      setServiceAdvance('');
      setWarningThreshold(warnTh.toString());
      setBlockThreshold(blkTh.toString());
      setDialogOpen(true);
    } catch (error) {
      setSnackbarMessage('Error fetching commission data');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedShop(null);
    setCommissionRate('');
    setMinimumPay('');
    setCashbackCount('');
    setMaximumPayEnabled(false);
    setMaximumPay('');
    setServiceAdvance('');
    setWarningThreshold('600');
    setBlockThreshold('500');
    setIsEditMode(false);
  };

  const handleDetailDialogOpen = (shop) => {
    setSelectedShop(shop);
    setDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setSelectedShop(null);
  };

  const handleCommissionRateChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
      setCommissionRate(value);
    }
  };

  const handleOpenConfirmDialog = () => {
    if (!commissionRate || !minimumPay || !cashbackCount) {
      setSnackbarMessage('Please enter all required fields');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (maximumPayEnabled && !(parseFloat(maximumPay) > parseFloat(minimumPay || 0))) {
      setSnackbarMessage('Maximum Pay must be greater than Minimum Pay');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmCommission = async () => {
    if (isEditMode ? !canEditCommission : !canSetCommission) {
      setSnackbarMessage('You do not have permission to perform this action');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    try {
      const payload = {
        shopId: selectedShop._id,
        commissionRate: parseFloat(commissionRate),
        minimumPay: parseFloat(minimumPay),
        cashbackCount: parseInt(cashbackCount, 10),
        maximumPayEnabled,
        ...(maximumPay !== '' ? { maximumPay: parseFloat(maximumPay) } : {}),
        serviceAdvance: serviceAdvance ? parseFloat(serviceAdvance) : 0,
        warningThreshold: parseFloat(warningThreshold) || 600,
        blockThreshold: parseFloat(blockThreshold) || 500,
      };

      if (isEditMode) {
        await axios.put(
          `${API_BASE_URL}/shop-important-data/${selectedShop._id}/${userId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem('token')}`,
            },
          }
        );
        setSnackbarMessage('Commission updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/shop-important-data/${userId}`, payload, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
          },
        });
        setSnackbarMessage('Commission set successfully');
      }

      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setTimeout(() => {
        fetchShops();
      }, 1000);

      handleConfirmDialogClose();
      handleDialogClose();
    } catch (error) {
      const errorMessage = (error && error.response && error.response.data && error.response.data.message) ? error.response.data.message : (isEditMode ? 'Error updating commission in database' : 'Error setting commission in database');
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      handleConfirmDialogClose();
    }
  };

  const columns = [
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
          {params.row.acemoneyUsercode || params.row.sellerIdentifier || params.row.vpaCode || params.row.merchantId || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'sellerName',
      headerName: 'Merchant Name',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'businessName',
      headerName: 'Shop Name',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'shopType',
      headerName: 'Shop Type',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {params.value || params.row.bussiness || 'General'}
        </Typography>
      ),
    },
    {
      field: 'stateCode',
      headerName: 'STATE',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || params.row.state || 'Kerala'}
        </Typography>
      ),
    },
    {
      field: 'district',
      headerName: 'DISTRICT',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'village',
      headerName: 'CONSTITUENCY',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {params.row.city || params.row.post || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'pincode',
      headerName: 'Pincode',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value || 'N/A'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'assignedQPayCenter',
      headerName: 'Q-Pay Center ID',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'assignedStateHead',
      headerName: 'State Head ID',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    // Set / Edit actions shown per permission; Details for everyone with page access
    ...((canSetCommission || canEditCommission) ? [{
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="info"
            size="small"
            onClick={() => handleDetailDialogOpen(params.row)}
          >
            Details
          </Button>
          {filterStatus === 'pending' ? (canSetCommission && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleSetCommissionOpen(params.row)}
            >
              Set
            </Button>
          )) : (canEditCommission && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEditCommissionOpen(params.row)}
            >
              Edit
            </Button>
          ))}
        </Box>
      ),
    }] : [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            color="info"
            size="small"
            onClick={() => handleDetailDialogOpen(params.row)}
          >
            Details
          </Button>
        ),
      }
    ]),
  ];

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              backgroundColor: 'rgba(255, 165, 0, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
            }}
          >
            <StorefrontIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Merchants Waiting for Commission Set
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Set and manage shop commission rates, thresholds, and cashback limits
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Filters and Search */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Merchant ID, Name, Shop, Pincode..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearchClear}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={handleFilterChange}
              aria-label="commission filter"
              fullWidth
              size="small"
            >
              <ToggleButton value="pending" aria-label="pending commission">
                Pending Commission ({totalRows})
              </ToggleButton>
              <ToggleButton value="set" aria-label="commission edit">
                Commission Edit
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredShops}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          pagination
          page={page}
          rowCount={totalRows}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          getRowId={(row) => row._id}
          disableSelectionOnClick
          loading={loading}
        />
      </Box>

      {/* Commission Setting/Editing Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Edit Commission' : 'Set Commission'} for {(selectedShop && selectedShop.businessName) ? selectedShop.businessName : 'Shop'}
          </Typography>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Commission Rate (%) *"
            type="number"
            value={commissionRate}
            onChange={handleCommissionRateChange}
            fullWidth
            required
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            sx={{ mt: 1 }}
            placeholder="Enter percentage (e.g., 2.5 for 2.5%)"
            helperText="Enter percentage (e.g., 2.5 for 2.5%)"
          />
          <TextField
            label="Minimum Pay (₹) *"
            type="number"
            value={minimumPay}
            onChange={(e) => setMinimumPay(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mt: 2 }}
            placeholder="Minimum transaction amount for commission"
            helperText="Minimum transaction amount for commission"
          />
          {/* Optional maximum pay: above this amount the customer gets no game card */}
          <Box sx={{ mt: 2, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: maximumPayEnabled ? '#fff7ed' : 'transparent' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={maximumPayEnabled}
                  onChange={(e) => setMaximumPayEnabled(e.target.checked)}
                  color="warning"
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Maximum Pay {maximumPayEnabled ? '(Active)' : '(Inactive)'}</Typography>}
            />
            <TextField
              label="Maximum Pay (₹)"
              type="number"
              value={maximumPay}
              onChange={(e) => setMaximumPay(e.target.value)}
              fullWidth
              disabled={!maximumPayEnabled}
              required={maximumPayEnabled}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ mt: 1 }}
              helperText="When active: payments above this amount get no game card (commission and other logic still run)"
            />
          </Box>
          <TextField
            label="Cashback Count *"
            type="number"
            value={cashbackCount}
            onChange={(e) => setCashbackCount(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0, step: 1 }}
            sx={{ mt: 2 }}
            placeholder="Number of cashback eligible transactions"
            helperText="Number of cashback eligible transactions"
          />
          {!isEditMode && (
            <TextField
              label="Service Advance (₹)"
              type="number"
              value={serviceAdvance}
              onChange={(e) => setServiceAdvance(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
              sx={{ mt: 2 }}
              placeholder="Initial deposit amount (0 to skip)"
              helperText="Initial advance amount to deposit (one-time, set during commission creation)"
            />
          )}
          <TextField
            label="Warning Threshold (₹)"
            type="number"
            value={warningThreshold}
            onChange={(e) => setWarningThreshold(e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: 1 }}
            sx={{ mt: 2 }}
            placeholder="600"
            helperText="Show warning banner in shop app below this balance"
          />
          <TextField
            label="Block Threshold (₹)"
            type="number"
            value={blockThreshold}
            onChange={(e) => setBlockThreshold(e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: 1 }}
            sx={{ mt: 2 }}
            placeholder="500"
            helperText="Block shop app access below this balance"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDialogClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleOpenConfirmDialog} color="primary" variant="contained" sx={{ fontWeight: 600 }}>
            {isEditMode ? 'Update Commission' : 'Set Commission'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm {isEditMode ? 'Commission Update' : 'Commission Details'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {isEditMode ? 'update' : 'set'} commission for <strong>{(selectedShop && selectedShop.businessName) ? selectedShop.businessName : 'Shop'}</strong> with:
          </Typography>
          <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid #ffa500' }}>
            <Typography variant="body2"><strong>Commission Rate:</strong> {commissionRate}%</Typography>
            <Typography variant="body2"><strong>Minimum Pay:</strong> ₹{minimumPay}</Typography>
            <Typography variant="body2"><strong>Maximum Pay:</strong> {maximumPayEnabled ? `₹${maximumPay} (Active)` : 'Inactive'}</Typography>
            <Typography variant="body2"><strong>Cashback Count:</strong> {cashbackCount}</Typography>
            {!isEditMode && serviceAdvance && (
              <Typography variant="body2"><strong>Service Advance:</strong> ₹{serviceAdvance}</Typography>
            )}
            <Typography variant="body2"><strong>Warning Threshold:</strong> ₹{warningThreshold}</Typography>
            <Typography variant="body2"><strong>Block Threshold:</strong> ₹{blockThreshold}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleConfirmDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmCommission} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shop Details Dialog */}
      <Dialog open={detailDialogOpen} onClose={handleDetailDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
            Shop Details
          </Typography>
          <IconButton onClick={handleDetailDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedShop && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Merchant ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.acemoneyUsercode || selectedShop.sellerIdentifier || selectedShop.vpaCode || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Merchant Name</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.sellerName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Shop Name</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.businessName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Shop Type</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.shopType || selectedShop.bussiness || 'General'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">State</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.stateCode || selectedShop.state || 'Kerala'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">District</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.district || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Pincode</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.pincode || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Q-Pay Center ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.assignedQPayCenter || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">State Head ID</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedShop.assignedStateHead || 'N/A'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDetailDialogClose} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionManagement;
